package ru.chm.gradereport.ui.screens.course.sheet

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import ru.chm.gradereport.ui.components.AppTextField
import ru.chm.gradereport.ui.components.ConfirmDialog
import ru.chm.gradereport.ui.components.LinkedText
import ru.chm.gradereport.ui.components.SecondaryButton
import ru.chm.gradereport.ui.theme.GradeTheme

/**
 * One submission link with optional inline editing.
 * Used in the grade edit dialog and in the submissions list dialog.
 */
@Composable
internal fun SubmissionRow(
    url: String,
    canEdit: Boolean,
    onEdit: (String) -> Unit,
    onDelete: (() -> Unit)?,
) {
    val colors = GradeTheme.colors
    var editing by remember(url) { mutableStateOf(false) }
    var text by remember(url) { mutableStateOf(url) }
    var confirmingDelete by remember(url) { mutableStateOf(false) }

    if (confirmingDelete) {
        ConfirmDialog(
            title = "Удалить ответ?",
            message = "Ссылка на ответ будет удалена без возможности восстановления:\n$url",
            confirmLabel = "Удалить",
            onConfirm = {
                confirmingDelete = false
                onDelete?.invoke()
            },
            onDismiss = { confirmingDelete = false },
        )
        return
    }

    if (editing) {
        Column(modifier = Modifier.fillMaxWidth().padding(top = 4.dp)) {
            AppTextField(
                value = text,
                onValueChange = { text = it },
                label = "Ссылка на ответ",
                singleLine = false,
            )
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 6.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
            ) {
                SecondaryButton(
                    text = "Сохранить",
                    enabled = text.isNotBlank(),
                    onClick = {
                        onEdit(text.trim())
                        editing = false
                    },
                    modifier = Modifier.weight(1f),
                )
                SecondaryButton(
                    text = "Отмена",
                    onClick = {
                        text = url
                        editing = false
                    },
                    modifier = Modifier.weight(1f),
                )
            }
        }
        return
    }

    val shape = RoundedCornerShape(10.dp)
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(top = 4.dp)
            .clip(shape)
            .background(colors.nestedCard, shape)
            .border(1.dp, colors.border, shape)
            .padding(horizontal = 10.dp, vertical = 8.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Column(modifier = Modifier.weight(1f)) {
            Text(
                "Ответ",
                color = SubmissionAccent,
                fontSize = 11.sp,
                fontWeight = FontWeight.Bold,
            )
            LinkedText(
                text = url,
                fontSize = 13.sp,
                lineHeight = 18.sp,
                modifier = Modifier.padding(top = 2.dp),
            )
        }
        if (canEdit) {
            Text(
                "Изменить",
                color = colors.blueAccent,
                fontSize = 13.sp,
                fontWeight = FontWeight.SemiBold,
                modifier = Modifier
                    .clip(RoundedCornerShape(8.dp))
                    .clickable { text = url; editing = true }
                    .padding(horizontal = 6.dp, vertical = 4.dp),
            )
            if (onDelete != null) {
                Text(
                    "Удалить",
                    color = colors.errorBanner,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier
                        .clip(RoundedCornerShape(8.dp))
                        .clickable { confirmingDelete = true }
                        .padding(horizontal = 6.dp, vertical = 4.dp),
                )
            }
        }
    }
}
